import React, {useState} from 'react';
import {IconSend} from '@tabler/icons-react';
import {Form} from '../types';
import {buildContextFields} from '../utils/formInitialization';

/**
 * The one-line way to add a comment under a comments screen. It posts through
 * the screen's create form like the full form would, with just the message
 * filled in; attachments and anything else still go through „Pridať".
 *
 * The message goes into the form field bound to the column marked
 * `comment-body` in admin (by screen column for a screen-bound form, by code
 * for a table-bound one). The form qualifies when, apart from its context
 * fields, nothing else is required - otherwise a comment could never be sent
 * from here.
 */
export const commentTextField = (form: Form | undefined, bodyColumn: { id: string; code: string } | undefined) => {
    if (!form || !bodyColumn) return null;
    const visible = form.fields.filter(f => !f.context && f.type !== 'one-to-many');
    const text = visible.find(f => f.screenColumnId ? f.screenColumnId === bodyColumn.id : f.code === bodyColumn.code);
    if (!text || (text.type !== 'text' && text.type !== 'string')) return null;
    if (visible.some(f => f !== text && f.required)) return null;
    return text;
};

interface CommentComposerProps {
    form: Form;
    /** The column marked `comment-body`, i.e. where the message lands. */
    bodyColumn: { id: string; code: string };
    /** Filter values the parent record is published under, e.g. {task_id: 267}. */
    parentFilters: Record<string, any>;
    getFilter: (code: string) => any;
    onSend: (data: Record<string, any>, form: Form) => Promise<boolean>;
}

export const CommentComposer: React.FC<CommentComposerProps> = ({form, bodyColumn, parentFilters, getFilter, onSend}) => {
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const textField = commentTextField(form, bodyColumn);

    if (!textField) return null;

    const send = async () => {
        const value = text.trim();
        if (!value || sending) return;

        setSending(true);
        try {
            // The parent comes from the embedding form first; the published
            // filters only fill in what it did not pass.
            const context = buildContextFields(form.fields, (code) => parentFilters[code] ?? getFilter(code));
            const ok = await onSend({...context, [textField.code]: value}, form);
            if (ok) setText('');
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void send();
        }
    };

    return (
        <div className="comment-composer d-flex align-items-end gap-2 mt-3" data-e2e="comment-composer">
            <textarea
                className="form-control"
                rows={1}
                placeholder="Napíš komentár… (Enter odošle, Shift+Enter nový riadok)"
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={sending}
                aria-label={textField.displayName}
            />
            <button
                type="button"
                className="btn btn-primary btn-icon flex-shrink-0"
                onClick={() => void send()}
                disabled={sending || text.trim() === ''}
                title="Odoslať"
                aria-label="Odoslať"
            >
                <IconSend size={18}/>
            </button>
        </div>
    );
};
